import {
    Bell,
    Building2,
    CreditCard,
    QrCode,
    UserPlus,
    Users,
    Home,
    AlertTriangle,
    UploadCloud,
    Columns3,
    type LucideIcon,
} from 'lucide-react';
import {
    LEAD_ROUTES,
    PROPERTY_ROUTES,
    QR_ROUTES,
    BUILDER_ROUTES,
    SALES_TEAM_ROUTES,
    BILLING_ROUTES,
    SETTINGS_ROUTES,
} from '@/api/constants/app.url';

/** One in-app notification, as returned by GET /api/v1/notifications. */
export interface Notification {
    id:          string;
    kind:        string;
    title:       string;
    body:        string | null;
    lead_id:     string | null;
    entity_type: string | null;
    entity_id:   string | null;
    read_at:     string | null;
    created_at:  string;
}

export interface NotificationListResponse {
    notifications: Notification[];
    unread_count:  number;
}

export interface ListNotificationsParams {
    limit?:  number;
    unread?: boolean;
}

/** Visual config keyed by entity_type — drives the icon + accent in the feed.
 *  We key on entity_type (a small, stable set) rather than the much larger kind
 *  enum, so new kinds render sensibly without a code change. */
interface EntityVisual {
    icon: LucideIcon;
    /** Tailwind classes for the icon chip (text + bg). */
    tone: string;
}

const ENTITY_VISUALS: Record<string, EntityVisual> = {
    lead:          { icon: UserPlus,      tone: 'text-emerald-600 bg-emerald-50' },
    property:      { icon: Home,          tone: 'text-brand-sky-500 bg-brand-sky-50' },
    qr_code:       { icon: QrCode,        tone: 'text-violet-600 bg-violet-50' },
    builder:       { icon: Building2,     tone: 'text-amber-600 bg-amber-50' },
    team_member:   { icon: Users,         tone: 'text-blue-600 bg-blue-50' },
    custom_column: { icon: Columns3,      tone: 'text-slate-600 bg-slate-100' },
    import:        { icon: UploadCloud,   tone: 'text-indigo-600 bg-indigo-50' },
    billing:      { icon: CreditCard,     tone: 'text-rose-600 bg-rose-50' },
};

const FALLBACK_VISUAL: EntityVisual = { icon: Bell, tone: 'text-ink-500 bg-ink-50' };
const SMS_FAILED_VISUAL: EntityVisual = { icon: AlertTriangle, tone: 'text-red-600 bg-red-50' };

/** Icon + tone for a notification, derived from its entity_type / kind. */
export function notificationVisual(n: Notification): EntityVisual {
    if (n.kind === 'sms_failed') return SMS_FAILED_VISUAL;
    return (n.entity_type && ENTITY_VISUALS[n.entity_type]) || FALLBACK_VISUAL;
}

/**
 * Where clicking a notification should navigate, derived from entity_type +
 * entity_id. Returns null when there's no useful destination (the row then
 * renders non-clickable). Property has no detail route, so it deep-links to the
 * list; lead has a real detail page.
 */
export function notificationHref(n: Notification): string | null {
    switch (n.entity_type) {
        case 'lead':          return n.entity_id ? LEAD_ROUTES.detail.to(n.entity_id) : LEAD_ROUTES.list;
        case 'property':      return PROPERTY_ROUTES.list;
        case 'qr_code':       return QR_ROUTES.list;
        case 'builder':       return BUILDER_ROUTES.list;
        case 'team_member':   return SALES_TEAM_ROUTES.list;
        case 'custom_column': return SETTINGS_ROUTES.root;
        case 'import':        return PROPERTY_ROUTES.list;
        case 'billing':       return BILLING_ROUTES.root;
        default:              return null;
    }
}

/** Compact relative time ("just now", "5m", "3h", "2d", or a date). */
export function timeAgo(iso: string): string {
    const then = new Date(iso).getTime();
    if (Number.isNaN(then)) return '';
    const secs = Math.max(0, Math.floor((Date.now() - then) / 1000));
    if (secs < 45) return 'just now';
    const mins = Math.floor(secs / 60);
    if (mins < 60) return `${mins}m`;
    const hours = Math.floor(mins / 60);
    if (hours < 24) return `${hours}h`;
    const days = Math.floor(hours / 24);
    if (days < 7) return `${days}d`;
    return new Date(iso).toLocaleDateString();
}
